import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchQuery } from '../../store/slices/ipoSlice';
import { toggleMobileMenu, setMobileMenuOpen, openContactForm } from '../../store/slices/uiSlice';
import {
  Search,
  Menu,
  X,
  ArrowUpRight,
  TrendingUp,
  ChevronDown,
  LayoutDashboard,
  CalendarDays,
  BarChart3,
  ClipboardCheck,
  Star,
  Layers,
  FileSearch,
  BadgeCheck,
  Scale,
  Newspaper,
  Flame,
  Landmark,
  FileText,
  LineChart,
  Trophy,
  Coins,
  ShieldCheck,
  Rocket,
  Mail,
} from 'lucide-react';

// Hover dropdowns for the main navigation; hash links scroll to landing-page sections
const NAV_MENUS = [
  {
    label: 'IPOs',
    to: '/',
    highlight: true,
    items: [
      { label: 'Dashboard', description: 'Mainboard & SME IPO overview', to: '/ipo-dashboard', icon: LayoutDashboard },
      { label: 'Upcoming IPOs', description: 'DRHP filings & SEBI approval pipeline', to: '/upcoming-ipos', icon: Rocket },
      { label: 'Timetable & Calendar', description: 'Open, close & allotment dates', to: '/ipo-calendar', icon: CalendarDays },
      { label: 'Live GMP Tracker', description: 'Live grey market premium & listing estimates', to: '/ipo-gmp', icon: TrendingUp, badge: 'Live' },
      { label: 'Subscription Data', description: 'Live QIB, NII & retail demand', to: '/ipo-subscription', icon: BarChart3 },
      // { label: 'Allotment Status', description: 'Check your share allotment', to: '/#allotment-status', icon: ClipboardCheck },
      { label: 'IPO Reviews', description: 'Apply or avoid analysis', to: '/ipo-reviews', icon: Star },
    ],
  },
  {
    label: 'SME IPOs',
    to: '/sme-ipos',
    items: [
      { label: 'SME IPO List', description: 'NSE Emerge & BSE SME issues', to: '/sme-ipos', icon: Layers },
      { label: 'SME GMP Tracker', description: 'Grey market rates for SME issues', to: '/sme-ipo-gmp', icon: TrendingUp, badge: 'Live' },
      { label: 'SME Subscription', description: 'Live QIB, NII & retail demand for SME issues', to: '/sme-ipo-subscription', icon: BarChart3 },
      { label: 'Upcoming SME IPOs', description: 'Opening soon & SME DRHP pipeline', to: '/sme-upcoming-ipos', icon: FileSearch },
      // { label: 'SME IPO Performance', description: 'Listing gains & returns of SME issues', to: '/ipo-performance?tab=sme', icon: LineChart },
      { label: 'SME IPO Reviews', description: 'Broker calls & CM scores for SME issues', to: '/sme-ipo-reviews', icon: Star },
    ],
  },
  {
    label: 'Reviews',
    to: '/',
    items: [
      { label: 'IPO Reviews & Ratings', description: 'In-depth issue analysis', to: '/ipo-reviews', icon: Star },
      // { label: 'Apply or Avoid Verdicts', description: 'Quick calls on open issues', to: '/ipo-reviews', icon: BadgeCheck },
      // { label: 'Peer Valuation Compare', description: 'P/E and margins vs listed peers', to: '/#peer-valuation', icon: Scale },
    ],
  },
  {
    label: 'News',
    to: '/ipo-news',
    items: [
      { label: 'Latest IPO News', description: 'Primary market updates', to: '/ipo-news', icon: Newspaper },
      { label: 'GMP Buzz', description: 'What grey desks are saying', to: '/#ipo-news', icon: Flame },
      { label: 'SEBI Filings & Approvals', description: 'DRHP and observation updates', to: '/upcoming-ipos?tab=approved', icon: Landmark },
    ],
  },
  {
    label: 'Reports',
    to: '/',
    items: [
      { label: 'DRHP Filings Tracker', description: 'Every draft prospectus filed', to: '/drhp-filings', icon: FileText },
      // { label: 'IPO Performance Tracker', description: 'Listing-day gains & current returns', to: '/ipo-performance', icon: LineChart },
      { label: 'Top Subscribed IPOs', description: 'Record-breaking demand', to: '/#top-subscribed', icon: Trophy },
    ],
  },
  // {
  //   label: 'NCD IPO',
  //   to: '/',
  //   items: [
  //     { label: 'Open NCD Issues', description: 'Bonds open for subscription', to: '/#ncd-open', icon: Coins },
  //     { label: 'Upcoming NCDs', description: 'Coupon rates & issue dates', to: '/#ncd-upcoming', icon: CalendarDays },
  //     { label: 'NCD Ratings Guide', description: 'Understand credit ratings', to: '/#ncd-ratings', icon: ShieldCheck },
  //   ],
  // },
];

const MenuBadge = ({ children }) => (
  <span className="text-[10px] leading-none bg-indigo-100 text-indigo-700 px-1.5 py-1 rounded-full font-bold uppercase tracking-wide">
    {children}
  </span>
);

export const HeaderNav = () => {
  const dispatch = useDispatch();
  const isMobileMenuOpen = useSelector((state) => state.ui.isMobileMenuOpen);
  const searchQuery = useSelector((state) => state.ipo.searchQuery);
  const [activeTab, setActiveTab] = useState('IPOs');
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();

  // Close any open dropdown after navigating
  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const openDropdown = (label) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };

  // Short delay so the pointer can travel from the link into the panel
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 150);
  };

  const handleMenuSelect = (label) => {
    clearTimeout(closeTimer.current);
    setActiveTab(label);
    setOpenMenu(null);
  };

  const handleSearchChange = (e) => {
    dispatch(setSearchQuery(e.target.value));
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-xs transition-all">
      {/* Full-width container with edge padding pushing items towards left & right */}
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28">
        <div className="relative flex items-center justify-between h-16 gap-2 sm:gap-4">

          {/* Left Side: Logo + Mobile Toggle */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Mobile Hamburger Button */}
            <button
              type="button"
              onClick={() => dispatch(toggleMobileMenu())}
              className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors"
              aria-label="Toggle navigation menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Brand Logo on the Left */}
            <Link to="/" className="flex items-center gap-2.5 group shrink-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#006666] via-[#008080] to-[#2dd4bf] flex items-center justify-center text-white shadow-md shadow-[#008080]/30 ring-1 ring-white/30 group-hover:scale-105 group-hover:shadow-[#008080]/50 transition-all duration-200 shrink-0">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 3.5L4 19.5H8.5L12 12L15.5 19.5H20L12 3.5Z"
                    fill="white"
                  />
                  <path
                    d="M7.8 15H16.2L14.2 19H9.8L7.8 15Z"
                    fill="#ccfbf1"
                  />
                  <circle cx="12" cy="7" r="1.2" fill="#008080" />
                </svg>
              </div>
              <div className="flex items-center tracking-tight">
                <span className="font-extrabold text-xl text-slate-900">Alambrix</span>
                <span className="ml-1 text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded uppercase tracking-wider">
                  IPO
                </span>
              </div>
            </Link>
          </div>

          {/* Center: Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center justify-center gap-0.5 2xl:gap-1.5 flex-1 min-w-0" aria-label="Main Navigation">
            {NAV_MENUS.map((menu) => {
              const isActive = activeTab === menu.label;
              const isOpen = openMenu === menu.label;
              return (
                <div
                  key={menu.label}
                  className="relative"
                  onMouseEnter={() => openDropdown(menu.label)}
                  onMouseLeave={scheduleClose}
                  onFocus={() => openDropdown(menu.label)}
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) scheduleClose();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setOpenMenu(null);
                  }}
                >
                  <Link
                    to={menu.to}
                    onClick={() => handleMenuSelect(menu.label)}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    className={`inline-flex items-center gap-0.5 2xl:gap-1 px-2.5 2xl:px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${isActive
                      ? 'bg-indigo-50 text-indigo-600 font-semibold shadow-xs'
                      : isOpen
                        ? 'text-slate-900 bg-slate-50'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                  >
                    {menu.label}
                    <ChevronDown
                      className={`w-3 h-3 2xl:w-3.5 2xl:h-3.5 opacity-60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </Link>

                  {/* Dropdown panel (pt-3 bridges the gap so hover isn't lost) */}
                  <div
                    className={`absolute left-1/2 top-full z-50 pt-3 -translate-x-1/2 transition-all duration-150 ${isOpen ? 'visible opacity-100 translate-y-0' : 'invisible opacity-0 -translate-y-1 pointer-events-none'
                      }`}
                  >
                    <div className="w-80 rounded-2xl bg-white border border-slate-200 shadow-elevated p-2">
                      {menu.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.label}
                            to={item.to}
                            onClick={() => handleMenuSelect(menu.label)}
                            className="group/item flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-teal-50/70 focus:bg-teal-50/70 focus:outline-none transition-colors"
                          >
                            <span className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 text-slate-500 group-hover/item:bg-white group-hover/item:text-[#008080] group-focus/item:text-[#008080] flex items-center justify-center flex-shrink-0 transition-colors">
                              <Icon className="w-4 h-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 group-hover/item:text-[#008080]">
                                {item.label}
                                {item.badge && <MenuBadge>{item.badge}</MenuBadge>}
                              </span>
                              <span className="block text-xs text-slate-500 mt-0.5">{item.description}</span>
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>

          {/* Right Side: Quick Search + Contact Us */}
          <div className="flex items-center gap-2.5 sm:gap-3.5 shrink-0 justify-end">

            {/* Search Bar (visible on md+) */}
            <div className="hidden sm:flex lg:hidden xl:flex items-center relative w-44 md:w-52 xl:w-36 2xl:w-60">
              <Search className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search IPOs..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3.5 py-1.5 text-sm bg-slate-50 hover:bg-slate-100/80 focus:bg-white border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-slate-400"
              />
            </div>

            {/* Opens the app-wide contact form (components/contact/ContactModal) */}
            <button
              type="button"
              onClick={() => dispatch(openContactForm())}
              className="inline-flex items-center gap-1.5 shrink-0 rounded-full bg-[#008080] hover:bg-[#006666] active:bg-[#004d4d] px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-white shadow-sm shadow-[#008080]/20 transition-colors"
            >
              <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
              Contact Us
            </button>

            {/* Primary Action Button */}
            {/* <a
              href="#apply-ipo"
              className="group inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-[#008080] via-[#008080] to-[#006666] hover:from-[#009696] hover:to-[#008080] active:from-[#006666] active:to-[#004d4d] text-white font-semibold text-sm px-3 sm:px-5 py-2 rounded-full shadow-md shadow-[#008080]/25 hover:shadow-lg hover:shadow-[#008080]/40 ring-1 ring-white/20 hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 shrink-0"
            >
              <span className="tracking-wide">Apply IPO</span>
              <ArrowUpRight className="hidden sm:block w-4 h-4 text-teal-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
            </a> */}
          </div>

        </div>
      </div>

      {/* Mobile Drawer Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white/98 backdrop-blur-md px-4 pt-3 pb-6 space-y-4 shadow-lg animate-fadeIn">
          {/* Mobile Search */}
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search companies, SME, or Mainboard..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Mobile Nav Links */}
          <div className="flex flex-col space-y-1">
            {NAV_MENUS.map((menu) => {
              const expanded = mobileExpanded === menu.label;
              return (
                <div key={menu.label}>
                  <button
                    type="button"
                    onClick={() => setMobileExpanded(expanded ? null : menu.label)}
                    aria-expanded={expanded}
                    className={`w-full px-3 py-2.5 rounded-lg text-base font-medium flex items-center justify-between ${activeTab === menu.label
                      ? 'bg-indigo-50 text-indigo-600 font-semibold'
                      : 'text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    <span className="flex items-center gap-2">
                      {menu.label}
                      {menu.highlight && (
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">
                          Live
                        </span>
                      )}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                  </button>

                  {expanded && (
                    <div className="mt-1 mb-2 ml-4 pl-3 border-l border-slate-200 space-y-0.5">
                      {menu.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.label}
                            to={item.to}
                            onClick={() => {
                              setActiveTab(menu.label);
                              dispatch(setMobileMenuOpen(false));
                            }}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-[#008080]"
                          >
                            <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="flex-1">{item.label}</span>
                            {item.badge && <MenuBadge>{item.badge}</MenuBadge>}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="pt-2 border-t border-slate-100">
            <a
              href="#apply-ipo"
              onClick={() => dispatch(setMobileMenuOpen(false))}
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white font-medium py-2.5 rounded-xl shadow-sm text-sm"
            >
              <TrendingUp className="w-4 h-4" />
              <span>Explore Active IPOs</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default HeaderNav;
